
// Pure "Capabilities → Action-groups → Context menu" planner for the
// Shift+Right-click geometry menus. No DOM, no THREE - Level-1 unit-testable.
//
//   selection context (vertex role / segment / range / whole feature)
//         │
//         ▼
//   EditingCapabilities  ─────────────►  this module
//         │                                   │
//         ▼                                   ▼
//   which actions are relevant          title + ordered, labelled action
//                                       groups (+ shared-node sub-menus)
//         │
//         ▼
//   VectorEditingMenu.js binds each action `id` to its onSelect closure and
//   hands the result to VertexContextMenu.js's showGroupedContextMenu().
//
// This is the ONE place that decides: the Danish context title, which group
// an action belongs to, and group order. VectorEditingMenu.js no longer makes
// any of those decisions inline.
//
// Basic vertex operations (Flyt / Snap) act ONLY on the explicitly selected
// vertex - there is deliberately no "kun dette punkt / hele forbindelsespunktet"
// scope choice. Geometric coincidence between endpoints is surfaced by the
// Topology-QA panel and the post-move toast, never by branching a basic
// edit onto other features (stabilisation task, §1.1 / §3).

import {resolveVertexRole, VERTEX_ROLE} from "./vertexSemantics.js";

import {t, T} from "./i18n/index.js";
// Group display labels (rendered small-caps by showGroupedContextMenu()).
export const MENU_GROUP = Object.freeze({
	POSITION: T("Position"),       // change position, structure unchanged
	GEOMETRY: T("Geometri"),       // change the geometry / its structure
	EDIT: T("Redigering"),         // segment-context wording for the same idea
	CONSTRUCT: T("Konstruktion"),  // use existing geometry to construct/derive
	STRUCTURE: T("Struktur"),      // structural organisation (direction, join)
	PLACEMENT: T("Positionering"),  // whole-feature placement
	SCOPE: T("Udvid valg"),        // widen the current selection
	REPORT: T("Rapport"),
	INTEGRATION: T("Integration"), // handlinger fra featurens integrationsbinding (adapter), fx "Rediger attributter"
});

const VERTEX_ORDER  = [MENU_GROUP.POSITION, MENU_GROUP.GEOMETRY, MENU_GROUP.INTEGRATION];
const SEGMENT_ORDER = [MENU_GROUP.EDIT, MENU_GROUP.CONSTRUCT, MENU_GROUP.PLACEMENT, MENU_GROUP.SCOPE];
const FEATURE_ORDER = [MENU_GROUP.GEOMETRY, MENU_GROUP.PLACEMENT, MENU_GROUP.STRUCTURE, MENU_GROUP.REPORT, MENU_GROUP.INTEGRATION];
const RANGE_ORDER   = [MENU_GROUP.GEOMETRY, MENU_GROUP.CONSTRUCT, MENU_GROUP.PLACEMENT];

// Buckets flat `items` (each {group, ...}) into the given ordered groups,
// dropping any that stay empty, then appends `trailingItems` (typically just
// "Slet …") as its own unlabelled group so it always sits behind a divider.
function assemble(order, items, trailingItems = []){
	const groups = order
		.map((label) => ({label: t(label), items: items.filter((it) => it.group === label)}))
		.filter((g) => g.items.length > 0);
	if(trailingItems.length) groups.push({items: trailingItems});
	return groups;
}

// ── Punkt / Linjepunkt / Endepunkt ───────────────────────────────────────
//
// `role` (from vertexSemantics.resolveVertexRole) decides the title and the
// "Flyt" vs "Flyt punkt" wording (also applied to the Z-only variant, "Flyt
// i Z…" vs "Flyt punkt i Z…"). There is no connection-group branch: a
// coincident endpoint is still just an Endepunkt here, and every POSITION
// action acts on this one vertex only.
export function planVertexMenu({
	geometryType,
	vertexIndex,
	vertexCount,
	caps,
	deleteRemovesWholeFeature = false,
	integrationActions = [],
} = {}){
	const role = resolveVertexRole(geometryType, vertexIndex, vertexCount);
	const g = MENU_GROUP;

	const title = role === VERTEX_ROLE.POINT_FEATURE ? t("Punkt")
		: role === VERTEX_ROLE.ENDPOINT ? t("Endepunkt")
			: t("Linjepunkt");
	const moveLabel = role === VERTEX_ROLE.POINT_FEATURE ? t("Flyt") : t("Flyt punkt");
	const moveZLabel = role === VERTEX_ROLE.POINT_FEATURE ? t("Flyt i Z…") : t("Flyt punkt i Z…");

	const items = [];

	if(caps.canMoveVertex) items.push({group: g.POSITION, id: "move", label: moveLabel});
	// "Flyt punkt i Z…" - any vertex (standalone/mellem/ende), a signed cm
	// value entered or picked in a popup dialog (see VectorEditingMenu.js's
	// runMovePointZ()/promptSignedDistanceCm()) - ellipsis since it opens one,
	// same convention as every other dialog-driven action in this menu.
	if(caps.canMoveVertexZ) items.push({group: g.POSITION, id: "moveZ", label: moveZLabel});
	// "Sæt kote…" - absolute Z (task §11), right after the relative-Z action
	// it's the sibling of.
	if(caps.canSetAbsoluteZ) items.push({group: g.POSITION, id: "setAbsoluteZ", label: t("Sæt kote…")});
	if(caps.canSnapToPointCloud) items.push({group: g.POSITION, id: "snapCloud", label: t("Snap til punktsky")});
	if(caps.canSnapToOtherPoint) items.push({group: g.POSITION, id: "snapPoint", label: t("Snap til andet punkt…")});

	if(caps.canSplit) items.push({group: g.GEOMETRY, id: "split", label: t("Del linje her")});
	if(caps.canContinueLine) items.push({group: g.GEOMETRY, id: "continueLine", label: t("Fortsæt linje…")});
	if(caps.canExtend) items.push({group: g.GEOMETRY, id: "extend", label: t("Forlæng til reference…")});
	// "Forlæng endesegment…" - fixed distance (cm), the endpoint's OWN
	// terminal-segment direction, no reference feature involved. Placed right
	// after "Forlæng til reference…" so the two related "extend" actions sit
	// together.
	if(caps.canExtendSegment) items.push({group: g.GEOMETRY, id: "extendSegment", label: t("Forlæng endesegment…")});
	if(caps.canTrim) items.push({group: g.GEOMETRY, id: "trim", label: t("Trim til reference…")});
	if(caps.canConnectEndpoint) items.push({group: g.GEOMETRY, id: "connect", label: t("Forbind til andet endepunkt…")});

	// Integrationens handlinger (fx "Rediger attributter") er objekt-handlinger: kun for et Punkt, hvis vertex-menu ER
	// objekt-menuen. En Linje/Polygon har dem i hel-objekt-menuen.
	if(role === VERTEX_ROLE.POINT_FEATURE){
		for(const action of integrationActions) items.push({group: g.INTEGRATION, id: "integration:" + action.id, label: action.title});
	}

	const trailing = [];
	if(caps.canDeleteVertex){
		trailing.push({id: "delete", label: deleteRemovesWholeFeature ? t("Slet objekt") : t("Slet punkt")});
	}else if(caps.canDeleteFeature){
		// geometri-låst objekt: kun hele objektet kan slettes (via integrationens sletning)
		trailing.push({id: "delete", label: t("Slet objekt")});
	}

	return {title, groups: assemble(VERTEX_ORDER, items, trailing)};
}

// ── Segment ──────────────────────────────────────────────────────────────
export function planSegmentMenu({caps} = {}){
	const g = MENU_GROUP;
	const items = [];

	if(caps.canInsertVertex) items.push({group: g.EDIT, id: "insert", label: t("Indsæt punkt")});
	if(caps.canSplit) items.push({group: g.EDIT, id: "split", label: t("Del linje her")});
	if(caps.canTrimBridge) items.push({group: g.EDIT, id: "trimBridge", label: t("Trim mod linje…")});

	// Parametrisk bue (task §7/§18) - only present when the clicked segment
	// belongs to a stored arc range (EditingCapabilities.js's arcRange gate).
	// Flat items in the same EDIT group, no submenu - "undgå unødvendige
	// undermenuer".
	if(caps.canEditArc) items.push({group: g.EDIT, id: "editArc", label: t("Rediger bue…")});
	if(caps.canFlipArc) items.push({group: g.EDIT, id: "flipArc", label: t("Vend bue")});
	if(caps.canDissolveArc) items.push({group: g.EDIT, id: "dissolveArc", label: t("Opløs bue")});

	if(caps.canFixedAngle) items.push({group: g.CONSTRUCT, id: "fixedAngle", label: t("Fast vinkel…")});
	if(caps.canParallel) items.push({group: g.CONSTRUCT, id: "parallel", label: t("Parallel med reference…")});
	if(caps.canPerpendicular) items.push({group: g.CONSTRUCT, id: "perpendicular", label: t("Vinkelret på reference…")});
	if(caps.canIntersectInsert) items.push({group: g.CONSTRUCT, id: "intersect", label: t("Sæt punkt ved skæring…")});

	// "Flyt segment i Z…" - only the segment's two vertices move (Positionering, like the whole-line "Flyt linje i Z…")
	if(caps.canMoveLineZ) items.push({group: g.PLACEMENT, id: "moveLineZ", label: t("Flyt segment i Z…")});

	if(caps.canSelectRange) items.push({group: g.SCOPE, id: "selectRange", label: t("Vælg sammenhængende del…")});
	if(caps.canSelectWhole) items.push({group: g.SCOPE, id: "selectWhole", label: t("Vælg hele objektet")});

	return {title: t("Segment"), groups: assemble(SEGMENT_ORDER, items, [])};
}

// ── Sammenhængende del (range) ───────────────────────────────────────────
export function planRangeMenu({caps, segmentCount = 0} = {}){
	const g = MENU_GROUP;
	const items = [];

	if(caps.canStraighten2D) items.push({group: g.GEOMETRY, id: "straighten2D", label: t("Ret ud i 2D")});
	if(caps.canStraighten3D) items.push({group: g.GEOMETRY, id: "straighten3D", label: t("Ret ud i 3D")});
	// task §18's explicit range-menu order: Ret ud i 2D/3D, Bue fra
	// pilhøjde…, Interpolér Z mellem punkter, Sæt fald/hældning…, then
	// Forenkle/Ortogonaliser/Fordel punkter lige/Omtegn del.
	if(caps.canArcFromSagitta) items.push({group: g.GEOMETRY, id: "arcFromSagitta", label: t("Bue fra pilhøjde…")});
	if(caps.canInterpolateZ) items.push({group: g.GEOMETRY, id: "interpolateZ", label: t("Interpolér Z mellem punkter")});
	if(caps.canSetSlope) items.push({group: g.GEOMETRY, id: "setSlope", label: t("Sæt fald/hældning…")});
	if(caps.canSimplify) items.push({group: g.GEOMETRY, id: "simplify", label: t("Forenkle")});
	if(caps.canOrthogonalize) items.push({group: g.GEOMETRY, id: "orthogonalize", label: t("Ortogonaliser")});
	if(caps.canDistributeEvenly) items.push({group: g.GEOMETRY, id: "distribute", label: t("Fordel punkter lige")});
	if(caps.canReshape) items.push({group: g.GEOMETRY, id: "reshape", label: t("Omtegn del…")});
	if(caps.canFixedAngle) items.push({group: g.CONSTRUCT, id: "fixedAngle", label: t("Fast vinkel…")});
	// "Flyt del i Z…" - only the range's vertices move
	if(caps.canMoveLineZ) items.push({group: g.PLACEMENT, id: "moveLineZ", label: t("Flyt del i Z…")});

	return {title: t("Sammenhængende del ({0} segmenter)", segmentCount), groups: assemble(RANGE_ORDER, items, [])};
}

// ── Hele objektet (Linje / Polygon / Tværprofil / Rør) ───────────────────
const OBJECT_TITLES = {point: T("Punkt"), linestring: T("Linje"), polygon: T("Polygon"), crossprofile: T("Tværprofil"), pipe: T("Rør"), "measure-fixed": T("Måling")};

export function planFeatureMenu({objectKind, caps, parametricShapeKind = null, integrationActions = []} = {}){
	const g = MENU_GROUP;
	const items = [];
	// "Rediger cirkel…/Opløs cirkel" vs "Rediger rektangel…/Opløs rektangel"
	// (task DEL B/C) - one shared shape-agnostic capability
	// (canEditParametricShape/canDissolveParametricShape - see
	// EditingCapabilities.js's own doc comment), just the wording picked
	// from `parametricShapeKind` here.
	const shapeLabel = parametricShapeKind === "rectangle" ? t("rektangel") : t("cirkel");
	if(caps.canEditParametricShape) items.push({group: g.GEOMETRY, id: "editParametricShape", label: t("Rediger {0}…", shapeLabel)});
	if(caps.canDissolveParametricShape) items.push({group: g.GEOMETRY, id: "dissolveParametricShape", label: t("Opløs {0}", shapeLabel)});

	if(caps.canStraighten2D) items.push({group: g.GEOMETRY, id: "straighten2D", label: t("Ret ud i 2D")});
	if(caps.canStraighten3D) items.push({group: g.GEOMETRY, id: "straighten3D", label: t("Ret ud i 3D")});
	// same order as planRangeMenu() - the whole feature is unified with the
	// range computation (see EditingCapabilities.js's own "Unification note").
	if(caps.canArcFromSagitta) items.push({group: g.GEOMETRY, id: "arcFromSagitta", label: t("Bue fra pilhøjde…")});
	if(caps.canInterpolateZ) items.push({group: g.GEOMETRY, id: "interpolateZ", label: t("Interpolér Z mellem punkter")});
	if(caps.canSetSlope) items.push({group: g.GEOMETRY, id: "setSlope", label: t("Sæt fald/hældning…")});
	if(caps.canSimplify) items.push({group: g.GEOMETRY, id: "simplify", label: t("Forenkle")});
	if(caps.canOrthogonalize) items.push({group: g.GEOMETRY, id: "orthogonalize", label: t("Ortogonaliser")});
	if(caps.canDistributeEvenly) items.push({group: g.GEOMETRY, id: "distribute", label: t("Fordel punkter lige")});
	if(caps.canFixedAngle) items.push({group: g.GEOMETRY, id: "fixedAngle", label: t("Fast vinkel…")});

	// "Flyt linje i Z…" - every vertex of the line moves by the same Δz (signed cm in a popup, like "Flyt punkt i Z…")
	if(caps.canMoveLineZ) items.push({group: g.PLACEMENT, id: "moveLineZ", label: t("Flyt linje i Z…")});
	if(caps.canSnapAllToPointCloud) items.push({group: g.PLACEMENT, id: "snapAll", label: t("Snap alle punkter til punktsky")});

	if(caps.canReverse) items.push({group: g.STRUCTURE, id: "reverse", label: t("Vend retning")});
	if(caps.canJoin) items.push({group: g.STRUCTURE, id: "join", label: t("Saml med anden linje…")});
	if(caps.canTrimBridge) items.push({group: g.STRUCTURE, id: "trimBridge", label: t("Trim mod linje…")});

	// "Vælg sammenhængende del" is deliberately NOT offered here (audit): from
	// the whole-object context it duplicates the segment menu's own SCOPE
	// entry, where the user is already pointing at the part they want to widen
	// from. Kept only in the segment/range contexts.

	if(caps.canShowReport) items.push({group: g.REPORT, id: "report", label: t("Vis rapport")});

	for(const action of integrationActions) items.push({group: g.INTEGRATION, id: "integration:" + action.id, label: action.title});

	const trailing = caps.canDeleteFeature ? [{id: "delete", label: t("Slet objekt")}] : [];

	return {title: OBJECT_TITLES[objectKind] ? t(OBJECT_TITLES[objectKind]) : t("Objekt"), groups: assemble(FEATURE_ORDER, items, trailing)};
}

// Zips a plan (structure only) with an `id -> onSelect` map into the shape
// showGroupedContextMenu() consumes ({title, groups:[{label?, items:[{label,
// tooltip?, onSelect | submenu:[{label, onSelect}]}]}]}). Unknown ids are
// dropped rather than throwing, so a plan entry with no wired handler simply
// does not appear (fail safe).
export function bindPlan(plan, onSelectById){
	const bindItem = (item) => {
		if(item.submenu){
			const sub = item.submenu
				.filter((s) => typeof onSelectById[s.id] === "function")
				.map((s) => ({label: s.label, tooltip: s.tooltip, onSelect: onSelectById[s.id]}));
			return sub.length ? {label: item.label, tooltip: item.tooltip, submenu: sub} : null;
		}
		const onSelect = onSelectById[item.id];
		return typeof onSelect === "function" ? {label: item.label, tooltip: item.tooltip, onSelect} : null;
	};

	const groups = plan.groups
		.map((grp) => ({label: grp.label, items: grp.items.map(bindItem).filter(Boolean)}))
		.filter((grp) => grp.items.length > 0);

	return {title: plan.title, groups};
}
